import { useEffect, useState } from "react";
import { Send, Trash2, Mail, Store, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { StatusBadge, Field, inputCls, thCls, tdCls } from "./ui";

export default function Owners() {
  const [data, setData] = useState(null);
  const [listings, setListings] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", listing_ids: [] });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/owners").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleListing = (id) =>
    setForm((f) => ({ ...f, listing_ids: f.listing_ids.includes(id) ? f.listing_ids.filter((x) => x !== id) : [...f.listing_ids, id] }));

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/owners/invite", form);
      toast.success(`Invite emailed to ${form.email}.`);
      setForm({ email: "", name: "", listing_ids: [] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendReports = async () => {
    try {
      const { data } = await api.post("/admin/owners/send-reports");
      toast.success(`Weekly report emailed to ${data.sent} owner${data.sent === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeOwner = async (email) => {
    if (!window.confirm(`Remove owner ${email}? Their listings stay published but become unlinked.`)) return;
    await api.delete(`/admin/owners/${email}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Owner removed.");
    load();
  };

  const removeInvite = async (id) => {
    if (!window.confirm("Delete this invite?")) return;
    await api.delete(`/admin/owners/invites/${id}`).catch(() => {});
    load();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="admin-owners">
      <div className="lg:col-span-5">
        <form onSubmit={invite} className="bg-white rounded-xl border border-[#E6DED3] shadow-sm p-6 space-y-4 lg:sticky lg:top-6" data-testid="owner-invite-form">
          <div className="flex items-center gap-2.5">
            <Mail className="w-5 h-5 text-ruby" />
            <p className="font-display font-bold text-ink">Invite a business owner</p>
          </div>
          <p className="text-xs text-stone2 leading-relaxed">
            They'll receive an email with a secure link to set their password. Any listings you tick below are linked to their account automatically.
          </p>
          <Field label="Owner name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Aung Kyaw" data-testid="invite-name-input" /></Field>
          <Field label="Owner email *"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="owner@business.com" data-testid="invite-email-input" /></Field>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-stone2 mb-2">Link listings (optional)</p>
            <div className="max-h-48 overflow-y-auto border border-sand rounded-xl divide-y divide-sand" data-testid="invite-listings-picker">
              {listings.map((l) => (
                <label key={l.id} className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-ink hover:bg-sand/50 cursor-pointer">
                  <input type="checkbox" checked={form.listing_ids.includes(l.id)} onChange={() => toggleListing(l.id)} className="w-4 h-4 accent-[#8B0018]" data-testid={`invite-listing-${l.id}`} />
                  <span className="truncate">{l.name}</span>
                  <span className="text-[10px] text-stone2 ml-auto shrink-0">{l.category_label}</span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-ruby hover:bg-ruby-dark disabled:opacity-60 text-cream font-bold py-3 rounded-xl" data-testid="send-invite-btn">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send invite email"}
          </button>
        </form>
      </div>

      <div className="lg:col-span-7 space-y-6">
        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
          <div className="px-5 py-4 border-b border-sand flex items-center justify-between">
            <p className="font-display font-bold text-ink">Owner accounts</p>
            <button onClick={sendReports} className="inline-flex items-center gap-2 text-xs font-semibold text-ruby hover:text-ruby-dark" data-testid="send-reports-btn" title="Owners also receive this automatically every Monday">
              <BarChart3 className="w-4 h-4" /> Send weekly reports now
            </button>
          </div>
          <table className="w-full text-left min-w-[480px]" data-testid="owners-table">
            <thead className="border-b border-sand">
              <tr><th className={thCls}>Owner</th><th className={thCls}>Email</th><th className={thCls}>Listings</th><th className={thCls}></th></tr>
            </thead>
            <tbody className="divide-y divide-sand">
              {(data?.owners || []).map((o) => (
                <tr key={o.id} data-testid={`owner-row-${o.email}`}>
                  <td className={`${tdCls} font-semibold`}>
                    <span className="flex items-center gap-2"><Store className="w-4 h-4 text-gold-dark" />{o.name}</span>
                  </td>
                  <td className={tdCls}>{o.email}</td>
                  <td className={tdCls}>{o.listings}</td>
                  <td className={tdCls}>
                    <button onClick={() => removeOwner(o.email)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`remove-owner-${o.email}`} title="Remove owner access"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && data.owners.length === 0 && <p className="px-5 py-8 text-sm text-stone2 text-center">No owner accounts yet — send your first invite.</p>}
        </div>

        <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
          <p className="px-5 py-4 border-b border-sand font-display font-bold text-ink">Invites</p>
          <table className="w-full text-left min-w-[480px]" data-testid="invites-table">
            <thead className="border-b border-sand">
              <tr><th className={thCls}>Email</th><th className={thCls}>Listings</th><th className={thCls}>Status</th><th className={thCls}></th></tr>
            </thead>
            <tbody className="divide-y divide-sand">
              {(data?.invites || []).map((inv) => (
                <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                  <td className={`${tdCls} font-semibold`}>{inv.email}</td>
                  <td className={tdCls}>{inv.listing_ids?.length || 0}</td>
                  <td className={tdCls}><StatusBadge status={inv.status} /></td>
                  <td className={tdCls}>
                    {inv.status === "pending" && (
                      <button onClick={() => removeInvite(inv.id)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-invite-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && data.invites.length === 0 && <p className="px-5 py-8 text-sm text-stone2 text-center">No invites sent yet.</p>}
        </div>
      </div>
    </div>
  );
}
